import { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Alert, Button, Input, Skeleton, Space, Switch, Tooltip, Typography } from 'antd';
import {
  CodeOutlined,
  DollarCircleOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';
import { trackEvent } from '@/lib/telemetry';
import { PageHeader } from '@/components/PageHeader';
import { SectionCard } from '@/components/SectionCard';
import { KpiTile } from '@/components/KpiTile';
import { BrandedEmpty } from '@/components/BrandedEmpty';
import { HelpHotspot } from '@/features/help/HelpHotspot';
import { ModelPricingEditorDrawer } from './ModelPricingEditorDrawer';
import { ModelFamilyCard } from './ModelFamilyCard';
import { ModelDetailDrawer } from './ModelDetailDrawer';
import { ModelEditorDrawer } from './ModelEditorDrawer';
import type { ModelFamily } from '@/stores/ModelsStore';
import type { ModelRow } from '@/api/models';

const { Text } = Typography;

// ─────────────────────────────────────────────────────────────────────────────
// The LLM model registry (ai.AIModel), for people rather than for SSMS.
//
// The table it replaces is 20 rows × 19 columns of integers and bit flags whose
// meaning is not on the screen: ApiId = 2 silently picks a provider AND an
// endpoint; a Temperature on a row with SupportsTemperature = 0 is decoration the
// runtime strips. So the page is built on two rules:
//
//   1. Group by model family. 20 rows are 6 models deployed across 7 tenants.
//   2. Never render a bare number where a sentence is the truth. Every reading
//      comes from ModelRegistryRules on the server, so the page cannot invent
//      its own account of what a column means.
//
// The health rail and the attention band exist because the most valuable things
// this data knows are absences — traffic with no rate, tenants with no active
// model — and an absence has no row to render.
// ─────────────────────────────────────────────────────────────────────────────

export const ModelsPage = observer(function ModelsPage() {
  const { modelsStore: store } = useStore();

  const [detailRow, setDetailRow] = useState<ModelRow | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<ModelRow | null>(null);
  const [rateSeed, setRateSeed] = useState<{ modelName: string; provider: string } | null>(null);
  const [rateOpen, setRateOpen] = useState(false);

  useEffect(() => {
    if (store.items.length === 0 && !store.isLoading) void store.fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function openEditor(row: ModelRow | null): void {
    setEditingRow(row);
    setEditorOpen(true);
    setDetailRow(null);
    trackEvent('Models.EditOpened', { id: row?.id ?? 'new' });
  }

  /** Phase-1 pricing affordance: author a rate without leaving the catalog. */
  function openRate(family: ModelFamily): void {
    // Seed from the model row so the join key cannot be typed wrong. Provider is
    // the string the cost lookup uses today, not the vendor name — see the
    // AiCostCalculator.NormalizeProvider note in the pricing review.
    setRateSeed({ modelName: family.name, provider: 'OpenAI' });
    setRateOpen(true);
    trackEvent('Models.RateOpened', { modelName: family.name });
  }

  const health = store.health;
  const unpricedWithTraffic = store.unpricedWithTraffic;
  const tenantGaps = health?.tenantsWithNoActiveModel ?? [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <PageHeader
        accent="violet"
        icon={<CodeOutlined />}
        title={
          <Space size={6} align="center">
            <span>Models</span>
            <HelpHotspot topic="models.overview" />
          </Space>
        }
        subtitle="Which models this platform can call, where each one runs, what it costs, and what it is actually being used for."
        count={store.families.length}
        countLabel={store.families.length === 1 ? 'model' : 'models'}
        actions={
          <Space wrap>
            <Input.Search
              allowClear
              placeholder="Search name, title or deployment slug…"
              style={{ width: 260 }}
              value={store.search}
              onChange={(e) => store.setSearch(e.target.value)}
              onSearch={() => void store.fetch()}
              prefix={<SearchOutlined style={{ color: colors.slate[400] }} />}
            />
            <Tooltip title="Hide deployments that are switched off">
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                <Switch
                  size="small"
                  checked={store.activeOnly}
                  onChange={(v) => {
                    store.setActiveOnly(v);
                    void store.fetch();
                  }}
                />
                Active only
              </span>
            </Tooltip>
            <Tooltip title="Show only rows with something worth looking at">
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                <Switch
                  size="small"
                  checked={store.conflictsOnly}
                  onChange={(v) => store.setConflictsOnly(v)}
                />
                Needs attention
              </span>
            </Tooltip>
            <Button icon={<ReloadOutlined />} loading={store.isLoading} onClick={() => void store.fetch()}>
              Refresh
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => openEditor(null)}>
              New model
            </Button>
          </Space>
        }
      />

      {store.error && (
        <Alert type="error" showIcon message="Failed to load models" description={store.error} closable />
      )}

      {/* ── Health rail ─────────────────────────────────────────────────── */}
      {health && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
            gap: 12,
          }}
        >
          <KpiTile
            label="Models"
            accent="violet"
            icon={<CodeOutlined />}
            value={health.families}
            hint={`${health.totalRows} deployment rows · ${health.activeRows} active`}
          />
          <KpiTile
            label="Agents wired"
            accent="ink"
            value={store.items.reduce((s, m) => s + m.agentsWired, 0)}
            hint={topWiredHint(store.items)}
          />
          <KpiTile
            label="Unpriced calls · 30d"
            accent={health.unpricedCalls30d > 0 ? 'red' : 'green'}
            icon={<DollarCircleOutlined />}
            value={health.unpricedCalls30d.toLocaleString()}
            hint={
              health.unpricedCalls30d === 0
                ? `all ${health.calls30d.toLocaleString()} calls costed`
                : noRateAtAll(unpricedWithTraffic) === 0
                  ? `of ${health.calls30d.toLocaleString()} — all predate their rate; nothing to fix`
                  : `of ${health.calls30d.toLocaleString()} — ${noRateAtAll(unpricedWithTraffic).toLocaleString()} on models with no rate at all`
            }
          />
          <KpiTile
            label="Needs attention"
            accent={health.rowsWithConflicts > 0 ? 'amber' : 'green'}
            icon={<WarningOutlined />}
            value={health.rowsWithConflicts}
            hint={
              health.rowsWithConflicts > 0
                ? 'rows whose settings contradict each other'
                : 'no contradictory settings'
            }
          />
        </div>
      )}

      {/* ── Attention band ──────────────────────────────────────────────── */}
      {(unpricedWithTraffic.length > 0 || tenantGaps.length > 0) && (
        <SectionCard title="Worth acting on" subtitle="Things the catalog cannot show you, because each one is an absence.">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {unpricedWithTraffic.map((u) => {
              // Three genuinely different situations wear the same "unpriced calls"
              // number, and only two of them are anyone's problem. Telling an
              // operator to set a rate that already exists is worse than silence.
              const alreadyPriced = u.hasCurrentRate;
              const effective = u.rateEffectiveDate
                ? new Date(u.rateEffectiveDate).toLocaleDateString()
                : null;

              return (
                <Finding
                  key={u.modelName}
                  tone={alreadyPriced ? colors.slate : colors.amber}
                  title={
                    alreadyPriced
                      ? `${u.modelName} — ${u.unpricedCalls30d.toLocaleString()} of ${u.calls30d.toLocaleString()} calls predate its rate`
                      : u.inRegistry
                        ? `${u.modelName} ran ${u.unpricedCalls30d.toLocaleString()} calls with no rate on file`
                        : `${u.modelName} ran ${u.unpricedCalls30d.toLocaleString()} calls and is not in the registry`
                  }
                  body={
                    alreadyPriced
                      ? `A rate has been on file since ${effective ?? 'recently'}, and calls made before that keep a NULL cost on purpose — a call should not acquire a price that was not in force when it ran. Nothing to do; the figure shrinks out of the 30-day window on its own.`
                      : u.inRegistry
                        ? 'Those calls recorded no cost, and reported spend counts them as $0. Adding a rate fixes it going forward — historical rows keep their honest NULL.'
                        : 'It has no ai.AIModel row and no rate, so nothing here governs its context window, capabilities or cost. Add the model, then a rate.'
                  }
                  action={
                    alreadyPriced ? undefined : (
                      <Button
                        size="small"
                        type="primary"
                        onClick={() => {
                          setRateSeed({ modelName: u.modelName, provider: 'OpenAI' });
                          setRateOpen(true);
                        }}
                      >
                        Set a rate
                      </Button>
                    )
                  }
                />
              );
            })}

            {tenantGaps.length > 0 && (
              <Finding
                tone={colors.slate}
                title={`${tenantGaps.length} ${tenantGaps.length === 1 ? 'tenant has' : 'tenants have'} model rows but none active`}
                body={`Merchant ${tenantGaps.map((t) => t.merchantId).join(', ')} — their agents have nothing to pick from even though the registry looks populated for them.`}
              />
            )}
          </div>
        </SectionCard>
      )}

      {/* ── Catalog ─────────────────────────────────────────────────────── */}
      {store.isLoading && store.items.length === 0 ? (
        <SectionCard>
          <Skeleton active paragraph={{ rows: 6 }} />
        </SectionCard>
      ) : store.families.length === 0 ? (
        <SectionCard>
          <BrandedEmpty
            icon={<CodeOutlined />}
            title={
              store.conflictsOnly
                ? 'Nothing needs attention'
                : store.search
                  ? 'No models match'
                  : 'No models registered'
            }
            description={
              store.conflictsOnly
                ? 'Every row in the catalog reads consistently. Turn the filter off to see them all.'
                : store.search
                  ? 'Try clearing the search, or switch off "Active only".'
                  : 'Register the first model so agents have something to call.'
            }
            action={
              !store.search && !store.conflictsOnly ? (
                <Button type="primary" icon={<PlusOutlined />} onClick={() => openEditor(null)}>
                  Register a model
                </Button>
              ) : undefined
            }
          />
        </SectionCard>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {store.families.map((f) => (
            <ModelFamilyCard
              key={f.name}
              family={f}
              onOpenRow={(row) => setDetailRow(row)}
              onEditRow={(row) => openEditor(row)}
              onSetRate={openRate}
            />
          ))}
        </div>
      )}

      <Text type="secondary" style={{ fontSize: 11.5 }}>
        Rates are stored in <code>ai.AiModelPricing</code> and copied onto each model row for display.
        Cost is calculated from the rate row at call time, so a new rate applies to calls made from
        then on — within about five minutes, while the pricing cache refreshes.
      </Text>

      <ModelDetailDrawer
        row={detailRow}
        open={detailRow != null}
        onClose={() => setDetailRow(null)}
        onEdit={(row) => openEditor(row)}
      />

      <ModelEditorDrawer
        open={editorOpen}
        editing={editingRow}
        onClose={() => {
          setEditorOpen(false);
          setEditingRow(null);
        }}
      />

      <ModelPricingEditorDrawer
        open={rateOpen}
        editing={null}
        seed={rateSeed}
        onClose={() => {
          setRateOpen(false);
          setRateSeed(null);
          // The card's price strip reads from the model catalog, so re-pull it
          // after the pricing drawer closes or a newly authored rate would not
          // show until the next manual refresh.
          void store.fetch();
        }}
      />
    </div>
  );
});

// ── bits ───────────────────────────────────────────────────────────────────

function Finding({
  tone,
  title,
  body,
  action,
}: {
  tone: Record<number, string>;
  title: string;
  body: string;
  action?: React.ReactNode;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 14,
        flexWrap: 'wrap',
        padding: '11px 13px',
        borderRadius: 9,
        border: `1px solid ${tone[600]}33`,
        background: `${tone[600]}0F`,
      }}
    >
      <div style={{ minWidth: 240, flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 3 }}>{title}</div>
        <div style={{ fontSize: 12.5, lineHeight: 1.5, color: colors.slate[500] }}>{body}</div>
      </div>
      {action}
    </div>
  );
}

/**
 * Unpriced calls that are actually a gap — i.e. on a model with NO rate at all.
 * Calls that merely predate an existing rate are not a gap and must not be
 * counted as one; conflating them turns a to-do list into noise.
 */
function noRateAtAll(unpriced: { unpricedCalls30d: number; hasCurrentRate: boolean }[]): number {
  return unpriced.filter((u) => !u.hasCurrentRate).reduce((s, u) => s + u.unpricedCalls30d, 0);
}

/** Names where the wiring actually sits, rather than restating the total. */
function topWiredHint(rows: ModelRow[]): string {
  const wired = rows.filter((r) => r.agentsWired > 0);
  if (wired.length === 0) return 'no agent is pinned to any model';
  const top = [...wired].sort((a, b) => b.agentsWired - a.agentsWired)[0];
  return wired.length === 1
    ? `all on ${top.name}`
    : `${top.agentsWired.toLocaleString()} on ${top.name}, across ${wired.length} deployments`;
}
